import { DEFAULT_CURRENCY_CODE, isDevMode, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { InputMaskModule } from '@ngneat/input-mask';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppFeatureService, AppService } from '@nk/access-block';
import { AnalysisApiClient, ApplicationApiClient } from '@nk/api-client-lib';
import { TopMenuModule } from '@nk/app/top-menu/top-menu.module';
import {
  APP_AUTH_CONFIG_TOKEN,
  AppAuthConfig,
  AppAuthService,
  AuthInterceptorService,
  GENERIC_AUTH_SERVICE_TOKEN,
} from '@nk/auth-lib';
import { CachingService } from '@nk/caching-lib';
import { CompanyPersonSearchBoxComponent, CompanyPersonSearchService } from '@nk/company-person-search-block';
import { ForceLogoutComponent } from '@nk/core/components/force-logout/force-logout.component';
import { LogoutViewComponent } from '@nk/core/components/logout-view/logout-view.component';
import { NkModalComponent } from '@nk/core/components/nk-modal/nk-modal.component';
import { CustomRouteReuseStrategy } from '@nk/core/custom/custom-route-reuse-strategy';
import { NoCacheInterceptor } from '@nk/core/interceptors/no-cache.interceptor';
import { AuthCallbackViewComponent } from '@nk/core/onboarding/auth-callback-view/auth-callback-view.component';
import { CallbackCorsAuthComponent } from '@nk/core/onboarding/callback-cors-auth/callback-cors-auth.component';
import { IdentityViewComponent } from '@nk/core/onboarding/identity-view/identity-view.component';
import { AdminGuardService } from '@nk/core/services/admin-guard.service';
import { AppContentService } from '@nk/core/services/app-content.service';
import { BusinessService } from '@nk/core/services/business.service';
import { CollateralService } from '@nk/core/services/collateral.service';
import { CompanyAffiliationService } from '@nk/core/services/company-affiliation.service';
import { CompanyAttributeService } from '@nk/core/services/company-attribute.service';
import { CompanyBrowserService } from '@nk/core/services/company-browser.service';
import { CompanyFlagService } from '@nk/core/services/company-flag.service';
import { CompanyService } from '@nk/core/services/company.service';
import { CustomReportService } from '@nk/core/services/custom-report.service';
import { DigestRedirectGuard } from '@nk/core/services/digest-redirect-guard.service';
import { DigestService } from '@nk/core/services/digest.service';
import { FeedbackService } from '@nk/core/services/feedback.service';
import { GeoService } from '@nk/core/services/geo.service';
import { IdentityGuardService } from '@nk/core/services/identity-guard.service';
import { LayoutService } from '@nk/core/services/layout.service';
import { MembershipGeoCountryService } from '@nk/core/services/membership-geo-country.service';
import { NaceCodeService } from '@nk/core/services/nace-code.service';
import { NkModalService } from '@nk/core/services/nk-modal.service';
import { PersonNetworkService } from '@nk/core/services/person-network.service';
import { PersonService } from '@nk/core/services/person.service';
import { RiskService } from '@nk/core/services/risk.service';
import { SensitiveDataService } from '@nk/core/services/sensitive-data.service';
import { SignUpService } from '@nk/core/services/sign-up.service';
import { TransientFileService } from '@nk/core/services/transient-file.service';
import { UrlHistoryService } from '@nk/core/services/url-history';
import { WatchlistService } from '@nk/core/services/watchlist.service';
import { LoginAuthenticatedComponent } from '@nk/core/views/login/components/login-authenticated.component';
import { LoginUnauthenticatedComponent } from '@nk/core/views/login/components/login-unauthenticated.component';
import { VerifyEmailErrorComponent } from '@nk/core/views/login/components/verify-email-error/verify-email-error.component';
import { LoginViewComponent } from '@nk/core/views/login/login-view.component';
import { AppPagePrinterService } from '@nk/report-block';
import { ScreeningService } from '@nk/screening-block';
import { CountryFocusItemsModule } from '@nk/shared/components/country-focus-items/country-focus-items.module';
import { FeedbackModule } from '@nk/shared/components/feedback/feedback.module';
import {
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  ReplaceDirective,
  SIDEBAR_TOGGLE_DIRECTIVES,
} from '@nk/shared/directives';
import { PipeUtilsModule } from '@nk/shared/pipes/pipe-utils.module';
import { TruncatePipe } from '@nk/shared/pipes/truncate.pipe';
import { SharedModule } from '@nk/shared/shared.module';
import {
  ButtonDirective,
  LayoutCenteredComponent,
  NkToastrModule,
  PasswordStrengthComponent,
  SpinnerComponent,
} from '@nk/ui-lib';
import {
  APP_ENV_CONFIG_TOKEN,
  AppEnvConfig,
  CustomBsDatepickerConfig,
  CustomTimepickerConfig,
  getStrippedHostname,
} from '@nk/utils-lib';
import { WebPortalBlockModule } from '@nk/web-portal-block';
import { appAuthConfig, environment } from '@nk/web-portal-env/environment';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TimepickerConfig } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import localeEn from '@angular/common/locales/en';
import localeNb from '@angular/common/locales/nb';
import {
  APP_CONTENT_API_SERVICE_TOKEN,
  APP_ICON_API_SERVICE_TOKEN,
  APP_PAGE_PRINTER_API_SERVICE_TOKEN,
  BANKRUPTCY_RISK_SCORE_API_SERVICE_TOKEN,
  COLLATERAL_API_SERVICE_TOKEN,
  COMPANY_API_SERVICE_TOKEN,
  COMPANY_EVENT_API_SERVICE_TOKEN,
  COMPANY_FLAG_API_SERVICE_TOKEN,
  COMPANY_LEGAL_RIGHT_API_SERVICE_TOKEN,
  COMPANY_OWNERSHIP_API_SERVICE_TOKEN,
  CUSTOMER_PERSON_API_SERVICE_TOKEN,
  DATA_SOURCE_API_SERVICE_TOKEN,
  FILE_API_SERVICE_TOKEN,
  GEO_API_SERVICE_TOKEN,
  MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN,
  PERSON_API_SERVICE_TOKEN,
  SCREENING_CONFIG_API_SERVICE_TOKEN,
  SENSITIVE_DATA_API_SERVICE_TOKEN,
  WATCHLIST_API_SERVICE_TOKEN,
} from '@nk/api-lib';
import { AppIconService } from '@nk/app/core/services/app-icon.service';
import { FileService } from '@nk/app/core/services/file.service';
import { CompanyLegalRightService, UPDATE_WATCHLIST_MAPPING_HANDLER_SERVICE_TOKEN } from '@nk/company-block';
import { CompanyAccountsService } from '@nk/core/services/company-accounts.service';
import { DataSourceService } from '@nk/data-source-lib';
import { LocaleProvider } from '@nk/localization-lib';
import { CustomerPersonService, ScreeningConfigService } from '@nk/screening-block';
import { NetworkRiskScoreService } from 'libs/company-block/src/lib/services/network-risk-score.service';
registerLocaleData(localeEn, 'en');
registerLocaleData(localeNb, 'nb');

const APP_ENV_CONFIG: AppEnvConfig = {
  ...environment,
  apiRootUrls: {
    local: 'http://localhost:15001',
    test: `https://api.test.enin.ai`,
    dev: `https://api.dev.enin.ai`,
    pre: `https://api.pre.enin.ai`,
    beta: `https://api.beta.enin.ai`,
    prod: `https://api.enin.ai`,
  },
};

const APP_AUTH_CONFIG: AppAuthConfig = {
  ...appAuthConfig,
};

const API_CONFIG: Provider[] = [
  CachingService,
  ApplicationApiClient,
  AnalysisApiClient,
  { provide: APP_ENV_CONFIG_TOKEN, useValue: APP_ENV_CONFIG },
  { provide: APP_AUTH_CONFIG_TOKEN, useValue: APP_AUTH_CONFIG },
  { provide: GENERIC_AUTH_SERVICE_TOKEN, useClass: AppAuthService },

  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptorService,
    multi: true,
  },
];

// Views and components that belong to core.
const APP_COMPONENTS = [LoginUnauthenticatedComponent, LoginAuthenticatedComponent];
const VIEWS = [
  IdentityViewComponent,
  CallbackCorsAuthComponent,
  IdentityViewComponent,
  LoginViewComponent,
  AuthCallbackViewComponent,
];

const APP_DIRECTIVES = [AsideToggleDirective, NAV_DROPDOWN_DIRECTIVES, ReplaceDirective, SIDEBAR_TOGGLE_DIRECTIVES];

const ANGULAR_MODULES = [BrowserModule, BrowserAnimationsModule];

const APP_MODULES = [AppRoutingModule, PipeUtilsModule];

const OTHERS_MODULES = [
  PaginationModule.forRoot(),
  BsDropdownModule.forRoot(),
  TooltipModule.forRoot(),
  BsDatepickerModule.forRoot(),
  TabsModule.forRoot(),
  ModalModule.forRoot(),
  TruncatePipe,
  InputMaskModule,
];

const APP_SERVICES = [
  DigestService,
  { provide: COMPANY_API_SERVICE_TOKEN, useClass: CompanyService },
  { provide: CompanyService, useClass: CompanyService }, // TODO: remove it and use only COMPANY_API_SERVICE_TOKEN
  { provide: WATCHLIST_API_SERVICE_TOKEN, useClass: WatchlistService },
  { provide: WatchlistService, useClass: WatchlistService }, // TODO: remove it and use only WATCHLIST_API_SERVICE_TOKEN
  { provide: BANKRUPTCY_RISK_SCORE_API_SERVICE_TOKEN, useClass: RiskService },
  { provide: COMPANY_EVENT_API_SERVICE_TOKEN, useClass: RiskService }, // TODO: remove it and use only COMPANY_EVENT_API_SERVICE_TOKEN when we move all company things to the company block
  { provide: RiskService, useClass: RiskService }, // TODO: remove it and use only BANKRUPTSY_RISK_SCORE_API_SERVICE_TOKEN when we move all company things to the company block
  { provide: COMPANY_FLAG_API_SERVICE_TOKEN, useClass: CompanyFlagService },
  { provide: CompanyFlagService, useClass: CompanyFlagService }, // TODO: remove it and use only COMPANY_API_SERVICE_TOKEN
  { provide: COMPANY_LEGAL_RIGHT_API_SERVICE_TOKEN, useClass: CompanyLegalRightService },
  { provide: PERSON_API_SERVICE_TOKEN, useClass: PersonService },
  { provide: PersonService, useClass: PersonService }, // TODO: remove it and use only COMPANY_API_SERVICE_TOKEN
  { provide: APP_ICON_API_SERVICE_TOKEN, useClass: AppIconService },
  { provide: AppIconService, useClass: AppIconService }, // TODO: remove it and use only COMPANY_API_SERVICE_TOKEN
  { provide: MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN, useClass: MembershipGeoCountryService },
  { provide: SCREENING_CONFIG_API_SERVICE_TOKEN, useClass: ScreeningConfigService },
  { provide: SENSITIVE_DATA_API_SERVICE_TOKEN, useClass: SensitiveDataService },
  { provide: CUSTOMER_PERSON_API_SERVICE_TOKEN, useClass: CustomerPersonService },
  { provide: GEO_API_SERVICE_TOKEN, useClass: GeoService },
  { provide: GeoService, useClass: GeoService }, // TODO: remove it and use only GEO_API_SERVICE_TOKEN
  { provide: COMPANY_OWNERSHIP_API_SERVICE_TOKEN, useClass: BusinessService }, // TODO: replace with CompanyOwnershipService
  { provide: DATA_SOURCE_API_SERVICE_TOKEN, useClass: DataSourceService },
  { provide: FILE_API_SERVICE_TOKEN, useClass: FileService },
  BusinessService,
  CompanyPersonSearchService,
  FeedbackService,
  CustomReportService,
  AppService,
  LayoutService,
  AppFeatureService,
  AppContentService,
  { provide: APP_CONTENT_API_SERVICE_TOKEN, useExisting: AppContentService },
  NkModalService,
  IdentityGuardService,
  DigestRedirectGuard,
  AdminGuardService,
  PersonNetworkService,
  CompanyAffiliationService,
  CompanyAttributeService,
  UrlHistoryService,
  TransientFileService,
  NaceCodeService,
  SignUpService,
  CompanyBrowserService,
  AppPagePrinterService,
  { provide: COLLATERAL_API_SERVICE_TOKEN, useClass: CollateralService },
  CollateralService,
  NetworkRiskScoreService,
  CompanyAccountsService,
  { provide: APP_PAGE_PRINTER_API_SERVICE_TOKEN, useClass: AppPagePrinterService },
  ...API_CONFIG,
];

// Warning: AoT requires an exported function for factories
export function translateHttpLoaderFactory(httpBackend: HttpBackend): MultiTranslateHttpLoader {
  let rootUrl = APP_ENV_CONFIG.apiRootUrls[APP_ENV_CONFIG.environmentKey];
  if (APP_ENV_CONFIG.environmentKey === 'local') {
    if (!isDevMode) {
      console.error('! Application environment is set as develop, but isDevMode is not True');
    }
  } else {
    if (!rootUrl) {
      console.warn('! No environment set.', APP_ENV_CONFIG);
      const strippedHostname = getStrippedHostname();
      rootUrl = `https://api.${strippedHostname}/application/`;
    } else {
      rootUrl = `${rootUrl}/application/`;
    }
  }

  return new MultiTranslateHttpLoader(httpBackend, [
    { prefix: '/app/assets/i18n/', suffix: '.json?cacheBuster=' + APP_ENV_CONFIG.cacheBusterTimestamp },
    {
      prefix: `${rootUrl}/v1/app-translation-file?cacheBuster=${APP_ENV_CONFIG.cacheBusterTimestamp}&language_code=`,
      suffix: ' ',
    },
  ]);
}

@NgModule({
  declarations: [
    AppComponent,
    ...VIEWS,
    ...APP_COMPONENTS,
    ...APP_DIRECTIVES,
    VerifyEmailErrorComponent,
    NkModalComponent,
    LogoutViewComponent,
    ForceLogoutComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    ...ANGULAR_MODULES,
    ...OTHERS_MODULES,
    ...APP_MODULES,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateHttpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    SharedModule,
    CountryFocusItemsModule,
    FeedbackModule,
    NkToastrModule,
    CompanyPersonSearchBoxComponent, // TODO: this should not be here
    WebPortalBlockModule,
    TopMenuModule,
    SpinnerComponent,
    ButtonDirective, // todo: refactor login component to remove this from here
    PasswordStrengthComponent,
    LayoutCenteredComponent,
  ],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    {
      provide: BsDatepickerConfig,
      useClass: CustomBsDatepickerConfig,
    },
    {
      provide: TimepickerConfig,
      useClass: CustomTimepickerConfig,
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: TokenInterceptor,
    //   multi: true,
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheInterceptor,
      multi: true,
    },
    ...APP_SERVICES,
    { provide: UPDATE_WATCHLIST_MAPPING_HANDLER_SERVICE_TOKEN, useClass: ScreeningService, multi: true },
    LocaleProvider,
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'NOK' },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
